/**
 * Everything a dialog has to get right, once, for both surfaces that are one.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `Drawer.tsx` carried this logic alone, and its own docblock said why nothing else should
 * copy it: *"a second overlay primitive in this codebase would be a second set of those
 * bugs."* That was right, and it was an argument against DUPLICATION rather than against a
 * second surface. When the console needed a centred modal as well as a right-hand panel, the
 * answer the comment implies is this file — one implementation of the contract, two shells
 * over it — not a modal that reimplements focus return and gets it subtly wrong.
 *
 * Each of these behaviours is here because dropping it is a bug somebody has already shipped
 * somewhere:
 *
 *   FOCUS RETURN      read during RENDER, not in the mount effect. React applies a child's
 *                     `autoFocus` during commit, which is before any effect runs, so an effect
 *                     reading `document.activeElement` finds the dialog's own first field —
 *                     and "restore focus" then restores to a node this dialog is about to
 *                     unmount. Focus lands on `<body>`, which is the exact failure the restore
 *                     exists to prevent, arrived at by implementing it.
 *
 *   FOCUS IN          the PANEL takes focus, not its first control, and only if nothing inside
 *                     already has it. Three drawers autofocus something deliberate — the metric
 *                     gate focuses the NOTE rather than the confirm button, so the operator has
 *                     to pass through stating why before they can reach it — and a shell that
 *                     grabbed the first focusable would quietly undo that.
 *
 *   THE TAB TRAP      recomputed per keystroke, never cached: the focusable set moves as fields
 *                     enable, a tab strip switches and the discard guard appears.
 *
 *   THE SCRIM PRESS   a click fires on the element the press and the release SHARE, so
 *                     selecting text in a field and dragging past the edge before letting go
 *                     delivers a click to the scrim and dismisses a form mid-edit. It is the
 *                     commonest way a dialog is closed by accident, and `stopPropagation` on
 *                     the panel cannot help because the release genuinely happened outside.
 *                     So only a press that STARTED on the scrim may dismiss.
 *
 *   THE DIRTY GUARD   `SiteMetadataPage` carries the incident: typed work destroyed by a stray
 *                     click, no warning, no way back. A dismiss with unsaved work raises a
 *                     strip rather than closing. Saving is never blocked — a caller's own
 *                     `onClose` after a successful mutation closes outright, because by then
 *                     there is nothing to lose.
 *
 * ── WHY ITS OWN FILE ────────────────────────────────────────────────────────
 * Fast Refresh re-evaluates a module that exports a component alongside a hook, losing the
 * component's state, and this repo lints that at `--max-warnings 0`. Same reason
 * `drawer-close.ts` sits beside `Drawer.tsx` rather than inside it.
 *
 * BODY SCROLL IS NOT LOCKED, here or anywhere. `overflow: hidden` on `<body>` removes the
 * scrollbar, which widens the page underneath by its own width — every Highcharts plot on it is
 * then drawn at the wrong size and the layout jumps sideways as the dialog opens. The scrim
 * swallows clicks; the wheel over the page beneath still scrolls it, which is the lesser fault
 * and the one nobody has ever complained about.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The reader is typing in a field OUTSIDE `panel` — a non-modal panel's page is still in use, and
 * Escape in its search box means "clear this", not "close the drawer".
 */
export function typingOutside(panel: Pick<Node, 'contains'> | null, active: Element | null): boolean {
  if (active === null || (panel !== null && panel.contains(active))) return false;
  const tag = active.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (active as HTMLElement).isContentEditable === true;
}

/** What can hold focus. Ordered as the DOM orders it, which is what Tab follows. */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export interface DialogChrome {
  /** Attach to the panel element. It is what takes focus and what the trap searches. */
  readonly panel: React.MutableRefObject<HTMLElement | null>;
  /**
   * False for exactly one frame after mount, so a shell has something to transition FROM.
   * Setting the final position in the same paint as the mount produces no animation at all —
   * the element simply appears where it belongs.
   */
  readonly shown: boolean;
  /** The guarded dismiss. Asks first when there is unsaved work; closes otherwise. */
  readonly requestClose: () => void;
  /** The unsaved-work strip is up. A shell renders its own; the state is owned here. */
  readonly asking: boolean;
  readonly setAsking: (v: boolean) => void;
  /** Put on the panel. Wraps Tab and Shift+Tab inside it. */
  readonly onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
  /** Put both on the SCRIM element. Together they refuse a dismiss that began inside. */
  readonly onScrimMouseDown: (e: React.MouseEvent<HTMLElement>) => void;
  readonly onScrimClick: (e: React.MouseEvent<HTMLElement>) => void;
}

export function useDialog({
  onClose,
  dirty = false,
  modal = true,
}: {
  /** Close for real. The guard runs before this, never after. */
  onClose: () => void;
  /** There is typed work in here that a dismiss would destroy. */
  dirty?: boolean;
  /**
   * False for a panel that sits BESIDE a page the reader keeps using (the work order drawer over
   * its board, 2026-10-13). Three things change, and only these:
   *
   *   focus return   goes to the last thing the reader touched OUTSIDE the panel, not to whatever
   *                  opened it first — they may have clicked three cards since, and the one they
   *                  are looking at is the one to land on;
   *   Escape         still closes, except while the reader is typing in a field OUTSIDE the panel
   *                  (the page's own search box), where Escape belongs to that field;
   *   the Tab trap   the shell does not attach `onKeyDown`; trapping focus in a panel whose page
   *                  is meant to stay usable would make the page unreachable from the keyboard.
   */
  modal?: boolean;
}): DialogChrome {
  const panel = useRef<HTMLElement | null>(null);
  const [asking, setAsking] = useState(false);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const requestClose = useCallback((): void => {
    if (dirty) setAsking(true);
    else onClose();
  }, [dirty, onClose]);

  /* Read once, during the first RENDER, while the control that opened this is still active.
     See the docblock — the mount effect is already too late. A non-modal panel then follows the
     reader: every focus that lands OUTSIDE it becomes the place to return to. */
  const [opener] = useState<Element | null>(() =>
    typeof document === 'undefined' ? null : document.activeElement,
  );
  const returnTo = useRef<Element | null>(opener);

  useEffect(() => {
    if (modal) return;
    const onFocusIn = (e: FocusEvent): void => {
      const t = e.target;
      if (t instanceof HTMLElement && panel.current !== null && !panel.current.contains(t)) returnTo.current = t;
    };
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, [modal]);

  useEffect(() => {
    if (panel.current !== null && !panel.current.contains(document.activeElement)) {
      panel.current.focus();
    }
    return () => {
      /* `focus()` on a detached node is a silent no-op rather than a throw, which is the
         degradation wanted: the row whose button opened this may have been removed by a save. */
      const back = returnTo.current;
      if (back instanceof HTMLElement && back.isConnected) back.focus();
    };
  }, []);

  /* Escape is a DISMISS, so it goes through the guard like every other dismiss. Captured on the
     window because focus is inside the panel and a native control may stop the bubble. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      if (!modal && typingOutside(panel.current, document.activeElement)) return;
      e.preventDefault();
      /* A second Escape while the strip is up backs out of the QUESTION, not out of the form.
         Escaping twice must never be a way to discard by accident. */
      if (asking) setAsking(false);
      else requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [asking, requestClose, modal]);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>): void => {
    if (e.key !== 'Tab' || panel.current === null) return;
    const items = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (first === undefined || last === undefined) return;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  const pressedScrim = useRef(false);

  const onScrimMouseDown = useCallback((e: React.MouseEvent<HTMLElement>): void => {
    pressedScrim.current = e.target === e.currentTarget;
  }, []);

  const onScrimClick = useCallback(
    (e: React.MouseEvent<HTMLElement>): void => {
      if (e.target !== e.currentTarget || !pressedScrim.current) return;
      requestClose();
    },
    [requestClose],
  );

  return {
    panel,
    shown,
    requestClose,
    asking,
    setAsking,
    onKeyDown,
    onScrimMouseDown,
    onScrimClick,
  };
}
